/**
 * English translations for Hashee Admin Console.
 * Keys follow the pattern: section.context or section.sub.context
 */
export const en: Record<string, string> = {
  // ─── Common ───
  "common.loading": "Loading...",
  "common.search": "Search",
  "common.apply": "Apply",
  "common.filter": "Filter",
  "common.refresh": "Refresh",
  "common.cancel": "Cancel",
  "common.confirm": "Confirm",
  "common.prev": "Prev",
  "common.next": "Next",
  "common.previous": "Previous",
  "common.view": "View",
  "common.yes": "Yes",
  "common.no": "No",
  "common.date": "Date",
  "common.status": "Status",
  "common.created": "Created",
  "common.action": "Action",
  "common.from": "From",
  "common.to": "To",
  "common.failed_to_load": "Failed to load",
  "common.action_failed": "Action failed",
  "common.no_data_for_period": "No data for this period",
  "common.show_details": "Show Details",
  "common.hide_details": "Hide Details",
  "common.all": "All",
  "stats.total": "total",

  // ─── Route Error Boundary ───
  "route_error.kicker": "Admin runtime error",
  "route_error.title": "This admin page failed to render",
  "route_error.description":
    "The console caught the page error before React Router showed its default crash screen. Reload after checking the failing route and API response.",
  "route_error.status": "Status",
  "route_error.message": "Message",
  "route_error.unknown_message": "Unknown route error",
  "route_error.back_home": "Back to Dashboard",
  "route_error.reload": "Reload page",

  // ─── Sidebar ───
  "sidebar.logo": "Hashee Ops",
  "sidebar.sign_out": "Sign out",
  "sidebar.collapse_nav": "Collapse navigation",
  "sidebar.expand_nav": "Expand navigation",
  "nav.dashboard": "Dashboard",
  "nav.metrics": "Metrics",
  "nav.integrations": "Integrations",
  "nav.users": "Users",
  "nav.users.all": "All Users",
  "nav.content": "Content",
  "nav.content.conversations": "Conversations",
  "nav.content.storage": "Storage",
  "nav.content.storage_orphan_audit": "Orphan Audit (Dry-run)",
  "nav.content.summary_jobs": "Summary Jobs",
  "nav.content.deliveries": "Deliveries",
  "nav.revenue": "Revenue",
  "nav.revenue.billing": "Billing",
  "nav.governance": "Governance",
  "nav.governance.relations": "Relations",
  "nav.governance.blocks": "Blocks",
  "nav.governance.agents": "Agents",
  "nav.governance.agent_reviews": "Agent Reviews",
  "nav.governance.grants": "Grants",
  "nav.governance.audit_log": "Audit Log",
  "nav.governance.space_reports": "Space Reports",
  "nav.system": "System",
  "nav.system.health": "Health",
  "nav.system.communications": "Communications",
  "nav.system.dlq": "Dead Letter Queue",
  "nav.system.crashes": "Crashes",
  "nav.system.backend_api_coverage": "Backend API Coverage",

  // ─── Login ───
  "login.title": "Hashee Ops Console",
  "login.subtitle": "Sign in with your admin account",
  "login.email": "Email",
  "login.password": "Password",
  "login.signing_in": "Signing in...",
  "login.sign_in": "Sign in",
  "login.dismiss_error": "Dismiss error",

  // ─── Dashboard ───
  "dashboard.title": "Dashboard",
  "dashboard.description": "System overview and key metrics",
  "dashboard.humans": "Humans",
  "dashboard.paid_users": "Paid Users",
  "dashboard.summary_failed_24h": "Summary Failed (24h)",
  "dashboard.system_status": "System Status",
  "dashboard.status_healthy": "Healthy",
  "dashboard.status_degraded": "Degraded",
  "dashboard.status_down": "Down",
  "dashboard.of_total": "of total",
  "dashboard.alerts": "Alerts",
  "dashboard.no_active_alerts": "No active alerts",
  "dashboard.failed_summary_jobs": "failed summary job(s) in the last 24 hours",
  "dashboard.view_summary_jobs": "View Summary Jobs",
  "dashboard.is_status": "is",
  "dashboard.cron_prefix": "Cron",
  "dashboard.poison_messages": "poison message(s) in queue",
  "dashboard.view_system_health": "View System Health",
  "dashboard.messages_today": "Messages (Today)",
  "dashboard.relations": "Relations",
  "dashboard.conversations": "Conversations",
  "dashboard.message_distribution": "Message Distribution (30d)",
  "dashboard.human_messages": "Human",
  "dashboard.agent_messages": "Agent",
  "dashboard.system_messages": "System",
  "dashboard.vs_last_week": "vs last week: {{value}}",

  // ─── Status Bar ───
  "dashboard.status_alerts": "{{count}} alert(s)",
  "dashboard.status_anomalous": "{{count}} KPI anomalous",
  "dashboard.status_normal": "{{count}} KPI normal",
  "dashboard.status_all_healthy": "All healthy",

  // ─── Dashboard Alerts ─��─
  "dashboard.all_systems_healthy": "All systems healthy",
  "dashboard.runbook_prefix": "Runbook:",

  // ─── Dashboard KPI Labels ───
  "dashboard.kpi_new_users": "New Users",
  "dashboard.kpi_new_agents": "New Agents",
  "dashboard.kpi_dau": "DAU",
  "dashboard.kpi_mrr": "MRR",
  "dashboard.group_label_short": "Group",

  // ─── Dashboard Social Trends ───
  "dashboard.social_trends_title": "Social Trends (30d)",
  "dashboard.dim_by_type": "By Type",
  "dashboard.dim_by_agent": "By Top Agent",
  "dashboard.dim_by_country": "By Country",
  "dashboard.h2a_label": "H2A Messages",
  "dashboard.h2h_label": "H2H Messages",
  "dashboard.group_label": "Group Messages",
  "dashboard.funnel_title": "Core Funnel",

  // ─── Funnel Mini ───
  "funnel.step_register": "Register",
  "funnel.step_conversation": "Conversation",
  "funnel.step_summary": "Summary",
  "funnel.step_delivery": "Delivery",
  "funnel.step_share": "Share",
  "funnel.step_storage": "Storage",
  "funnel.step_payment": "Payment",
  "funnel.conversion_rate": "\u2193 {{percent}}%",
  "funnel.empty_hint":
    "Funnel data is computed daily by cron. If empty, check System > Health for cron status.",

  // ─── Trend Card ───
  "trend.up": "\u2191",
  "trend.down": "\u2193",
  "trend.flat": "\u2014",

  // ─── Growth Trend Chart ───
  "growth_trend.title": "Growth Trend (30 days)",
  "growth_trend.chart_label": "Growth trend chart showing DAU and new users",
  "growth_trend.chart_title": "Growth Trend",
  "growth_trend.dau": "DAU",
  "growth_trend.new_users": "New Users",

  // ─── Suggestions ───
  "suggestions.title": "Suggestions",
  "suggestions.loading": "Loading suggestions...",
  "suggestions.ops": "Ops Suggestions",
  "suggestions.product": "Product Suggestions",
  "suggestions.no_suggestions": "No suggestions at this time",

  // ─── Confirm Dialog ───
  "confirm.reason_label": "Reason (minimum {{min}} characters)",
  "confirm.reason_placeholder": "Explain why this action is necessary...",
  "confirm.chars_remaining_one": "{{count}} more character required",
  "confirm.chars_remaining_other": "{{count}} more characters required",

  // ─── Pagination (DataTable) ───
  "pagination.prev": "Prev",
  "pagination.next": "Next",

  // ─── Table (TableParts) ───
  "table.loading": "Loading...",

  // ─── Global Filters ───
  "filters.platform": "Platform",
  "filters.country": "Country",
  "filters.language": "Language",
  "filters.all_platforms": "All Platforms",
  "filters.all_countries": "All Countries",
  "filters.all_languages": "All Languages",
  "filters.platform_ios": "iOS",
  "filters.platform_ipados": "iPadOS",
  "filters.platform_android": "Android",
  "filters.platform_macos": "macOS",
  "filters.platform_windows": "Windows",
  "filters.platform_admin": "Admin",

  // ─── Not Found ───
  "not_found.title": "404",
  "not_found.text": "Page not found",
  "not_found.back": "Back to Dashboard",

  // ─── Placeholder ───
  "placeholder.management": "management",
  "placeholder.wave2": "This page will be implemented in Wave 2.",

  // ─── Users ───
  "users.title": "Users",
  "users.description": "Manage user accounts",
  "users.search_placeholder": "Search by name, email, or ID...",
  "users.all_status": "All Status",
  "users.all_plans": "All Plans",
  "users.status_active": "Active",
  "users.status_frozen": "Frozen",
  "users.status_deactivated": "Deactivated",
  "users.plan_free": "Free",
  "users.plan_pro": "Pro",
  "users.plan_premium": "Premium",
  "users.plan_max": "Max",
  "users.sort_by": "Sort by {{label}}",
  "users.no_users_found": "No users found.",
  "users.loading": "Loading...",
  "users.col_name": "Name",
  "users.col_email": "Email",
  "users.col_custom_id": "Custom ID",
  "users.col_plan": "Plan",
  "users.col_tokens": "Tokens",
  "users.col_storage": "Storage",
  "users.col_status": "Status",
  "users.col_created": "Created",
  "users.page_info": "{{total}} user(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── User Detail ───
  "user_detail.loading": "Loading...",
  "user_detail.not_found": "User not found.",
  "user_detail.failed_to_load": "Failed to load user",
  "user_detail.deactivated": "Deactivated",
  "user_detail.issue_warning": "Issue Warning",
  "user_detail.freeze_account": "Freeze Account",
  "user_detail.unfreeze_account": "Unfreeze Account",
  "user_detail.suspend_account": "Suspend Account",
  "user_detail.deactivate_account": "Deactivate Account",
  "user_detail.terminate_account": "Terminate Account",
  "user_detail.freeze_title": "Freeze Account",
  "user_detail.unfreeze_title": "Unfreeze Account",
  "user_detail.freeze_desc":
    "This will immediately revoke all sessions and disconnect the user.",
  "user_detail.unfreeze_desc":
    "This will restore the user's access. They will need to log in again.",
  "user_detail.user_id": "User ID",
  "user_detail.custom_id": "Custom ID",
  "user_detail.status": "Status",
  "user_detail.created": "Created",
  "user_detail.tab_overview": "Overview",
  "user_detail.tab_summary_jobs": "Summary Jobs",
  "user_detail.tab_audit": "Audit",
  "user_detail.tab_billing": "Billing",
  "user_detail.plan": "Plan",
  "user_detail.token_balance": "Token Balance",
  "user_detail.storage": "Storage",
  "user_detail.devices": "Devices",
  "user_detail.no_summary_jobs": "No summary jobs.",
  "user_detail.no_audit_records": "No audit records.",
  "user_detail.no_billing_events": "No billing events.",
  "user_detail.col_status": "Status",
  "user_detail.col_target": "Target",
  "user_detail.col_created": "Created",
  "user_detail.col_action": "Action",
  "user_detail.col_type": "Type",
  "user_detail.col_amount": "Amount",
  "user_lifecycle.optional": "Optional",
  "user_lifecycle.field.reason": "Reason",
  "user_lifecycle.field.policy_section": "Policy Section",
  "user_lifecycle.field.incident_summary": "Incident Summary",
  "user_lifecycle.field.suspension_end": "Suspension End",
  "user_lifecycle.field.appeal_deadline": "Appeal Deadline",
  "user_lifecycle.field.data_export_url": "Data Export URL",
  "user_lifecycle.deactivate.title": "Deactivate Account",
  "user_lifecycle.deactivate.description":
    "Deactivate {{name}} and revoke account access after audit review.",
  "user_lifecycle.deactivate.confirm": "Deactivate",
  "user_lifecycle.issue-warning.title": "Issue Warning",
  "user_lifecycle.issue-warning.description":
    "Send {{name}} a policy warning without changing account access.",
  "user_lifecycle.issue-warning.confirm": "Send Warning",
  "user_lifecycle.suspend.title": "Suspend Account",
  "user_lifecycle.suspend.description":
    "Suspend {{name}} until the specified end time.",
  "user_lifecycle.suspend.confirm": "Suspend",
  "user_lifecycle.terminate.title": "Terminate Account",
  "user_lifecycle.terminate.description":
    "Terminate {{name}} after confirming appeal and export details.",
  "user_lifecycle.terminate.confirm": "Terminate",

  // ─── Agents ───
  "agents.title": "Agents",
  "agents.description": "Agent governance management",
  "agents.tab_list": "Agents",
  "agents.tab_reports": "Reports",
  "agents.search_placeholder": "Search by name or creator...",
  "agents.status_filter": "Filter by governance status",
  "agents.all_status": "All Status",
  "agents.no_agents_found": "No agents found.",
  "agents.loading": "Loading...",
  "agents.col_name": "Name",
  "agents.col_creator": "Creator",
  "agents.col_connection": "Connection",
  "agents.col_status": "Status",
  "agents.col_risk": "Risk",
  "agents.col_reports": "Reports",
  "agents.col_created": "Created",
  "agents.failed_to_load": "Failed to load agents",
  "agent_reports.agent_id_placeholder": "Filter by agent_id",
  "agent_reports.type_filter": "Filter by report type",
  "agent_reports.type_all": "All report types",
  "agent_reports.total": "{{total}} reports",
  "agent_reports.empty": "No agent reports found.",
  "agent_reports.empty_value": "None",
  "agent_reports.failed": "Failed to load agent reports",
  "agent_reports.unknown_name": "Unknown",
  "agent_reports.col_report": "Report",
  "agent_reports.col_agent": "Agent",
  "agent_reports.col_reporter": "Reporter",
  "agent_reports.col_type": "Type",
  "agent_reports.col_message": "Message",
  "agent_reports.col_description": "Description",
  "agent_reports.type.credential_phishing": "Credential phishing",
  "agent_reports.type.system_impersonation": "System impersonation",
  "agent_reports.type.spam": "Spam",
  "agent_reports.type.unauthorized_collection": "Unauthorized collection",
  "agent_reports.type.other": "Other",

  // ─── Agent Reviews ───
  "agent_reviews.title": "Agent Reviews",
  "agent_reviews.description": "Review public Agent listing submissions",
  "agent_reviews.failed_to_load": "Failed to load Agent reviews",
  "agent_reviews.action_failed": "Review action failed",
  "agent_reviews.empty": "No listing reviews found.",
  "agent_reviews.status_filter": "Filter by review status",
  "agent_reviews.col_agent": "Agent",
  "agent_reviews.col_creator": "Creator",
  "agent_reviews.col_version": "Version",
  "agent_reviews.col_status": "Review Status",
  "agent_reviews.col_reasons": "Reasons",
  "agent_reviews.col_submitted": "Submitted",
  "agent_reviews.approve": "Approve",
  "agent_reviews.reject": "Reject",
  "agent_reviews.approve_title": "Approve Agent listing",
  "agent_reviews.reject_title": "Reject Agent listing",
  "agent_reviews.approve_desc":
    "This publishes the submitted Agent version in Discover as a free public listing.",
  "agent_reviews.reject_desc":
    "This rejects the submitted Agent version. Add reviewer notes for the creator.",
  "agent_reviews.status.submitted": "Submitted",
  "agent_reviews.status.auto_check_failed": "Auto-check Failed",
  "agent_reviews.status.human_review": "Human Review",
  "agent_reviews.status.approved": "Approved",
  "agent_reviews.status.rejected": "Rejected",
  "agent_reviews.status.withdrawn": "Withdrawn",

  // ─── Agent Detail ───
  "agent_detail.created_by": "Created by {{name}}",
  "agent_detail.loading": "Loading...",
  "agent_detail.not_found": "Agent not found.",
  "agent_detail.failed_to_load": "Failed to load agent",
  "agent_detail.banned": "Banned",
  "agent_detail.restrict_agent": "Restrict Agent",
  "agent_detail.restore_agent": "Restore Agent",
  "agent_detail.unpublish_agent": "Unpublish Listing",
  "agent_detail.ban_agent": "Ban Agent",
  "agent_detail.rotate_identity": "Rotate Identity",
  "agent_detail.activate_paid_access": "Activate Paid Access",
  "agent_detail.restrict_title": "Restrict Agent",
  "agent_detail.restore_title": "Restore Agent",
  "agent_detail.unpublish_title": "Unpublish Agent Listing",
  "agent_detail.ban_title": "Ban Agent",
  "agent_detail.restrict_desc":
    "This will restrict the agent from operating. Existing connections will be affected.",
  "agent_detail.restore_desc": "This will restore the agent to active status.",
  "agent_detail.unpublish_desc":
    "This removes the Agent from Discover and disables paid monetization. Existing private or protected use is not promoted.",
  "agent_detail.ban_desc":
    "Permanently ban Agent {{name}} ({{id}}). The creator will be notified, all related conversations frozen, and the action recorded in audit_records. This is a stronger action than Restrict and is intended for ToS violations.",
  "agent_detail.agent_id": "Agent ID",
  "agent_detail.status": "Status",
  "agent_detail.listing_status": "Listing Status",
  "agent_detail.monetization_status": "Monetization Status",
  "agent_detail.connection": "Connection",
  "agent_detail.risk_score": "Risk Score",
  "agent_detail.reports": "Reports",
  "agent_detail.description": "Description",
  "agent_detail.created": "Created",
  "agent_detail.h2a_active": "Active",
  "agent_detail.h2a_suspended": "Suspended",
  "agent_detail.h2a_terminated": "Terminated",
  "agent_detail.tab_overview": "Overview",
  "agent_detail.tab_governance_history": "Governance History",
  "agent_detail.tab_reports": "Reports",
  "agent_detail.connection_mode": "Connection Mode",
  "agent_detail.report_count": "Report Count",
  "agent_detail.last_updated": "Last Updated",
  "agent_detail.no_governance_history": "No governance history.",
  "agent_detail.no_reports": "No reports.",
  "agent_detail.col_reporter": "Reporter",
  "agent_detail.col_reason": "Reason",
  "agent_detail.col_status": "Status",
  "agent_detail.col_date": "Date",
  "agent_paid_access.title": "Activate paid access",
  "agent_paid_access.description":
    "Move {{name}} from paid review into paid_active after confirming entitlement delivery is ready.",
  "agent_paid_access.reason": "Audit reason",
  "agent_paid_access.entitlement_path_ready":
    "Entitlement delivery path is ready and verified",
  "agent_paid_access.confirm": "Activate Paid Access",
  "agent_identity_rotation.title": "Rotate Agent identity",
  "agent_identity_rotation.description":
    "Replace the advertised identity key bundle for {{name}}. This action requires a complete prekey bundle and writes an admin audit record.",
  "agent_identity_rotation.confirm": "Rotate Identity",
  "agent_identity_rotation.new_ik_x25519_public_key": "X25519 identity key",
  "agent_identity_rotation.new_ik_ed25519_public_key": "Ed25519 identity key",
  "agent_identity_rotation.new_spk_id": "Signed prekey id",
  "agent_identity_rotation.new_spk_public_key": "Signed prekey",
  "agent_identity_rotation.new_spk_signature": "Signed prekey signature",
  "agent_identity_rotation.new_spk_expires_at": "Signed prekey expiry",
  "agent_identity_rotation.identity_binding_proof": "Identity binding proof",
  "agent_identity_rotation.justification": "Justification",

  // ─── Grants ───
  "grants.title": "Token Grants",
  "grants.description": "Data access grants",
  "grants.placeholder_user_id": "user_id",
  "grants.placeholder_agent_id": "agent_id",
  "grants.placeholder_scope_type": "scope_type",
  "grants.placeholder_status": "status",
  "grants.no_grants_found": "No grants found",
  "grants.col_grant_id": "Grant ID",
  "grants.col_user": "User",
  "grants.col_agent": "Agent",
  "grants.col_scope": "Scope",
  "grants.col_expires": "Expires",
  "grants.col_status": "Status",
  "grants.never": "Never",
  "grants.loading_detail": "Loading detail...",
  "grants.scope_value": "Scope value:",
  "grants.created": "Created:",
  "grants.summary_jobs": "Summary Jobs",
  "grants.no_summary_jobs": "No associated summary jobs",

  // ─── Audit ───
  "audit.title": "Audit Log",
  "audit.description": "Administrative action history",
  "audit.placeholder_action": "action",
  "audit.placeholder_actor_id": "actor_id",
  "audit.placeholder_target_id": "target_id",
  "audit.start_date": "Start date",
  "audit.end_date": "End date",
  "audit.col_action": "Action",
  "audit.col_actor": "Actor",
  "audit.col_target": "Target",
  "audit.col_metadata": "Metadata",
  "audit.col_date": "Date",
  "audit.no_records_found": "No audit records found",

  // ─── Metrics Layout ───
  "metrics.title": "Metrics",
  "metrics.description": "Analytics and performance data",
  "metrics.tab_growth": "Growth",
  "metrics.tab_funnel": "Funnel",
  "metrics.tab_revenue": "Revenue",
  "metrics.tab_system": "System",
  "metrics.tab_behavior": "Behavior",
  "metrics.tab_messages": "Messages",
  "metrics.tab_feature_value": "Feature Value",
  "metrics.tab_segments": "Segments",
  "metrics.tab_cohort": "Cohort",

  // ─── Growth Tab ───
  "growth.avg_dau": "Avg DAU",
  "growth.new_users": "New Users",
  "growth.messages": "Messages",
  "growth.summary_requests": "Summary Requests",
  "growth.daily_breakdown": "Daily Breakdown",
  "growth.col_date": "Date",
  "growth.col_dau": "DAU",
  "growth.col_new_users": "New Users",
  "growth.col_messages": "Messages",
  "growth.col_summary_req": "Summary Req",

  // ─── Funnel Tab ───
  "funnel.funnel_steps": "Funnel Steps",
  "funnel.no_funnel_data": "No funnel data available",
  "funnel.label_funnel": "Funnel",
  "funnel.activation": "Activation",
  "funnel.content_loop": "Content Loop",
  "funnel.monetization": "Monetization",

  // ─── Revenue Tab ───
  "revenue.mrr": "MRR",
  "revenue.paid_users": "Paid Users",
  "revenue.checkouts": "Checkouts",
  "revenue.topups": "Top-ups",
  "revenue.daily_breakdown": "Daily Breakdown",
  "revenue.col_date": "Date",
  "revenue.col_mrr": "MRR",
  "revenue.col_paid_users": "Paid Users",
  "revenue.col_checkouts": "Checkouts",
  "revenue.col_topups": "Top-ups",

  // ─── System Tab ───
  "system_tab.summary_requests": "Summary Requests",
  "system_tab.success_rate": "Success Rate",
  "system_tab.storage": "Storage",
  "system_tab.webhook_failures": "Webhook Failures",
  "system_tab.daily_breakdown": "Daily Breakdown",
  "system_tab.col_date": "Date",
  "system_tab.col_summary_req": "Summary Req",
  "system_tab.col_success": "Success",
  "system_tab.col_failed": "Failed",
  "system_tab.col_storage": "Storage",
  "system_tab.col_wh_failures": "WH Failures",

  // ─── Behavior Tab ───
  "behavior.sessions_per_user": "Sessions / User / Day",
  "behavior.messages_per_user": "Messages / User / Day",
  "behavior.summaries_per_user": "Summaries / User / Day",
  "behavior.bookmarks_per_user": "Bookmarks / User / Day",
  "behavior.shares_per_user": "Shares / User / Day",
  "behavior.agent_usage_rate": "Agent Usage Rate",
  "behavior.retention_rate": "Retention Rate",
  "behavior.message_breakdown": "Message Breakdown",
  "behavior.human_messages": "Human Messages",
  "behavior.agent_messages": "Agent Messages",
  "behavior.group_messages": "Group Messages",
  "behavior.daily_breakdown": "Daily Breakdown",
  "behavior.col_date": "Date",
  "behavior.col_sessions": "Sessions/U",
  "behavior.col_msgs": "Msgs/U",
  "behavior.col_summaries": "Summaries/U",
  "behavior.col_bookmarks": "Bookmarks/U",
  "behavior.col_shares": "Shares/U",
  "behavior.col_agent_rate": "Agent Rate",
  "behavior.col_retention": "Retention",

  // ─── Feature Value Tab ───
  "feature_value.title": "Feature Value Analysis",
  "feature_value.col_feature": "Feature",
  "feature_value.col_exposure": "Exposure",
  "feature_value.col_adoption": "Adoption",
  "feature_value.col_repeat": "Repeat",
  "feature_value.col_retention_lift": "Retention Lift",
  "feature_value.col_status": "Status",
  "feature_value.core_feature": "Core Feature",
  "feature_value.exposed_but_weak": "Exposed but Weak",
  "feature_value.used_but_shallow": "Used but Shallow",
  "feature_value.neutral": "Neutral",

  // ─── Segment Tab ───
  "segment.distribution": "Segment Distribution",
  "segment.no_data": "No segmentation data for this date",
  "segment.users_title": "Users",
  "segment.power_user": "Power User",
  "segment.builder": "Builder",
  "segment.organizer": "Organizer",
  "segment.reader": "Reader",
  "segment.chat_only": "Chat Only",
  "segment.col_user_id": "User ID",
  "segment.col_messages": "Messages",
  "segment.col_summaries": "Summaries",
  "segment.col_deliveries": "Deliveries",
  "segment.col_shares": "Shares",
  "segment.col_favorited": "Favorited",
  "segment.no_users": "No users in this segment",

  // ─── Cohort Tab ───
  "cohort.cohorts": "Cohorts",
  "cohort.total_users": "Total Users",
  "cohort.avg_week1_retention": "Avg Week 1 Retention",
  "cohort.avg_week4_retention": "Avg Week 4 Retention",
  "cohort.retention_matrix": "Retention Matrix",
  "cohort.col_cohort_week": "Cohort Week",
  "cohort.col_users": "Users",
  "cohort.no_cohort_data": "No cohort data",

  // ─── Billing Page ───
  "billing.title": "Billing",
  "billing.description": "User billing lookup and actions",
  "billing.search_placeholder": "Enter user_id...",
  "billing.user": "User",
  "billing.plan": "Plan",
  "billing.status": "Status",
  "billing.token_balance": "Token Balance",
  "billing.subscription_usage": "Subscription Usage",
  "billing.topup_remaining": "Top-up Remaining",
  "billing.grant_tokens": "Grant Tokens",
  "billing.resync_entitlement": "Resync Entitlement",
  "billing.developer_credit": "Credit Developer",
  "billing.token_amount_label": "Token amount to grant:",
  "billing.grant_title": "Grant Tokens",
  "billing.resync_title": "Resync Entitlement",
  "billing.replay_title": "Replay Webhook",
  "billing.grant_desc": "Grant {{amount}} tokens to this user.",
  "billing.resync_desc": "Re-sync entitlement from Stripe.",
  "billing.replay_desc": "Replay this failed webhook event.",
  "billing.developer_credit_title": "Credit Developer Balance",
  "billing.developer_credit_desc":
    "Credit developer balance for user {{userId}} with an audited reason.",
  "billing.developer_credit_confirm": "Credit Balance",
  "billing.developer_credit_amount_minor": "Amount Minor",
  "billing.developer_credit_currency": "Currency",
  "billing.developer_credit_reason": "Reason",
  "billing.tab_user_billing": "User Billing",
  "billing.tab_agent_commerce": "Agent Commerce",
  "billing.agent_pricing_title": "Agent Subscription Price Mapping",
  "billing.agent_pricing_desc":
    "Map reviewed Agent subscription SKUs to Stripe price and product IDs.",
  "billing.agent_pricing_sku": "Hashee SKU",
  "billing.agent_pricing_stripe_price": "Stripe Price ID",
  "billing.agent_pricing_stripe_product": "Stripe Product ID",
  "billing.agent_pricing_reason": "Reason",
  "billing.agent_pricing_save": "Save Mapping",
  "billing.agent_pricing_success": "Saved mapping for {{sku}}.",

  // ─── Billing Tables ───
  "billing_table.token_ledger": "Token Ledger",
  "billing_table.col_type": "Type",
  "billing_table.col_amount": "Amount",
  "billing_table.col_balance_after": "Balance After",
  "billing_table.col_description": "Description",
  "billing_table.col_date": "Date",
  "billing_table.no_ledger_entries": "No ledger entries",
  "billing_table.usage_events": "Usage Events",
  "billing_table.payment_transactions": "Payment Transactions",
  "billing_table.entitlement_events": "Entitlement Events",
  "billing_table.webhook_events": "Webhook Events",
  "billing_table.col_provider": "Provider",
  "billing_table.col_source": "Source",
  "billing_table.col_resource": "Resource",
  "billing_table.col_provider_model": "Provider / Model",
  "billing_table.col_units": "Units",
  "billing_table.col_price_version": "Price Version",
  "billing_table.col_finalized": "Finalized",
  "billing_table.col_sku": "SKU",
  "billing_table.col_transaction": "Transaction",
  "billing_table.col_entitlement": "Entitlement",
  "billing_table.col_verified": "Verified",
  "billing_table.col_plan": "Plan",
  "billing_table.col_token_delta": "Token Delta",
  "billing_table.col_storage_limit": "Storage Limit",
  "billing_table.col_preview_minutes": "Preview Minutes",
  "billing_table.col_source_transaction": "Source Txn",
  "billing_table.col_effective": "Effective",
  "billing_table.col_event_id": "Event ID",
  "billing_table.col_event_type": "Event Type",
  "billing_table.col_status": "Status",
  "billing_table.col_received": "Received",
  "billing_table.col_processed": "Processed",
  "billing_table.col_action": "Action",
  "billing_table.replay": "Replay",
  "billing_table.no_usage_events": "No usage events",
  "billing_table.no_payment_transactions": "No payment transactions",
  "billing_table.no_entitlement_events": "No entitlement events",
  "billing_table.no_webhook_events": "No webhook events",

  // ─── Storage Page ───
  "storage.title": "Storage",
  "storage.description": "Monitor user storage usage and sync status",
  "storage.filter_all": "All",
  "storage.filter_over_quota": "Over Quota",
  "storage.filter_sync_error": "Sync Error",
  "storage.no_users_found": "No users found.",
  "storage.col_user": "User",
  "storage.col_used_limit": "Used / Limit",
  "storage.col_msg": "Msg",
  "storage.col_media": "Media",
  "storage.col_art": "Art",
  "storage.col_kb": "KB",
  "storage.col_sync": "Sync",

  // ─── Storage Orphan Audit (Dry-run) ───
  "storage_orphan_audit.title": "Storage Orphan Audit (Dry-run)",
  "storage_orphan_audit.description":
    "R2 objects with no matching storage_objects row. Read-only. Cron runs Sundays at 07:00 UTC.",
  "storage_orphan_audit.banner_dry_run":
    "V1.1 dry-run — no automatic deletion. Investigation only.",
  "storage_orphan_audit.no_runs_yet":
    "No orphan audit runs found yet. Cron runs Sundays at 07:00 UTC.",
  "storage_orphan_audit.latest_run": "Latest run",
  "storage_orphan_audit.history": "Recent runs (30d)",
  "storage_orphan_audit.prefix_breakdown": "Top prefixes",
  "storage_orphan_audit.no_prefix_data":
    "No prefix breakdown recorded for the latest run.",
  "storage_orphan_audit.no_history_data":
    "No previous runs in the last 30 days.",
  "storage_orphan_audit.col_prefix": "Prefix",
  "storage_orphan_audit.col_count": "Orphan count",
  "storage_orphan_audit.col_bytes": "Total bytes",
  "storage_orphan_audit.col_run_at": "Run at",
  "storage_orphan_audit.col_oldest": "Oldest object",
  "storage_orphan_audit.col_environment": "Environment",
  "storage_orphan_audit.env_prod": "Production",
  "storage_orphan_audit.env_nonprod": "Non-production",
  "storage_orphan_audit.partial_scan_warning":
    "Partial scan: storage_objects table exceeded 50k known-keys cap; orphan count may be inflated.",

  // ─── Storage Detail Panel ───
  "storage_detail.breakdown": "Breakdown",
  "storage_detail.failed_to_load": "Failed to load detail.",
  "storage_detail.recalculate": "Recalculate",
  "storage_detail.run_cleanup": "Run Cleanup",
  "storage_detail.recalculate_title": "Recalculate Storage",
  "storage_detail.recalculate_desc":
    "This will recalculate total storage usage from object sizes. Existing usage value will be overwritten.",
  "storage_detail.cleanup_title": "Run Cleanup",
  "storage_detail.cleanup_desc":
    "This will mark all soft-deleted objects for physical deletion. This action cannot be undone.",
  "storage_detail.tab_trace": "Trace",
  "storage_detail.tab_breakdown": "Breakdown",
  "storage_detail.tab_sync": "Sync",
  "storage_detail.no_breakdown": "No breakdown available.",
  "storage_detail.object_count_bytes": "{{count}} objects, {{bytes}}",
  "storage_detail.no_recent_changes": "No recent objects.",
  "storage_detail.no_sync_cursors": "No sync cursors.",
  "storage_detail.col_object": "Object",
  "storage_detail.col_size": "Size",
  "storage_detail.col_state": "State",
  "storage_detail.col_trace": "Source",
  "storage_detail.col_created": "Created",
  "storage_detail.col_device": "Device",
  "storage_detail.col_conversation": "Conversation",
  "storage_detail.col_last_seq": "Last seq",
  "storage_detail.col_synced_at": "Synced at",
  "storage_detail.trace_unresolved": "Unresolved",
  "storage_detail.storage_object_id": "Storage object",
  "storage_detail.conversation_id": "Conversation",
  "storage_detail.ref_id": "Ref",
  "storage_detail.message_id": "Message",
  "storage_detail.upload_id": "Upload",
  "storage_detail.deleted_at": "Deleted {{date}}",
  "storage_detail.source_message_archive": "Message archive",
  "storage_detail.source_file_upload": "File upload",
  "storage_detail.source_cloud_preview_session": "Cloud Preview",
  "storage_detail.source_public_space_item": "Public Space",
  "storage_detail.status_pending": "Pending",
  "storage_detail.status_finalizing": "Finalizing",
  "storage_detail.status_confirmed": "Confirmed",
  "storage_detail.status_aborted": "Aborted",
  "storage_detail.status_pending_delete": "Pending delete",
  "storage_detail.status_deleted": "Deleted",
  "storage_detail.recalculated_message":
    "Recalculated: {{previous}} -> {{current}}",
  "storage_detail.cleanup_message": "Cleanup: {{count}} objects marked",

  // ─── Summary Jobs Page ───
  "summary_jobs.title": "Summary Jobs",
  "summary_jobs.description":
    "Monitor AI summarization jobs and retry failures",
  "summary_jobs.filter_all": "All",
  "summary_jobs.filter_pending": "Pending",
  "summary_jobs.filter_running": "Running",
  "summary_jobs.filter_completed": "Completed",
  "summary_jobs.filter_failed": "Failed",
  "summary_jobs.user_id_placeholder": "Filter by user ID...",
  "summary_jobs.no_jobs_found": "No jobs found.",
  "summary_jobs.col_target": "Target",
  "summary_jobs.col_user": "User",
  "summary_jobs.col_status": "Status",
  "summary_jobs.col_model": "Model",
  "summary_jobs.col_tokens_in": "Tokens In",
  "summary_jobs.col_tokens_out": "Tokens Out",
  "summary_jobs.col_created": "Created",

  // ─── Summary Job Detail Panel ───
  "summary_job_detail.failed_to_load": "Failed to load detail.",
  "summary_job_detail.error": "Error",
  "summary_job_detail.grant": "Grant",
  "summary_job_detail.scope": "Scope",
  "summary_job_detail.expires": "Expires",
  "summary_job_detail.thread": "Thread",
  "summary_job_detail.thread_status": "Status",
  "summary_job_detail.preview": "Preview",
  "summary_job_detail.retry_job": "Retry Job",
  "summary_job_detail.retry_title": "Retry Summary Job",
  "summary_job_detail.retry_desc":
    "This will create a new pending job with the same target. The failed job record will remain for audit.",

  // ─── Deliveries Page ───
  "deliveries.title": "Deliveries",
  "deliveries.description": "Manage delivery content",
  "deliveries.all_types": "All Types",
  "deliveries.user_id_placeholder": "Filter by user ID...",
  "deliveries.no_deliveries_found": "No deliveries found.",
  "deliveries.loading_detail": "Loading detail...",
  "deliveries.col_title": "Title",
  "deliveries.col_type": "Type",
  "deliveries.col_user": "User",
  "deliveries.col_conversation": "Conversation",
  "deliveries.col_pinned": "Pinned",
  "deliveries.col_hidden": "Hidden",
  "deliveries.col_deleted": "Deleted",
  "deliveries.col_created": "Created",
  "deliveries.detail_user": "User",
  "deliveries.detail_conversation": "Conversation",
  "deliveries.detail_mime": "MIME",
  "deliveries.detail_size": "Size",
  "deliveries.detail_updated": "Updated",
  "deliveries.reindex": "Reindex",
  "deliveries.unhide": "Unhide",
  "deliveries.unpin": "Unpin",
  "deliveries.reindex_title": "Reindex Delivery",
  "deliveries.unhide_title": "Unhide Delivery",
  "deliveries.unpin_title": "Unpin Delivery",
  "deliveries.reindex_desc":
    "This will trigger re-indexing of the delivery content.",
  "deliveries.unhide_desc":
    "This will make the delivery visible to the user again.",
  "deliveries.unpin_desc": "This will remove the pin from the delivery.",

  // ─── Integrations Page ───
  "integrations.title": "Integrations",
  "integrations.description": "SDK, plugin, and CLI metrics",
  "integrations.tab_sdk": "SDK",
  "integrations.tab_openclaw": "OpenClaw",
  "integrations.tab_claude_code": "Claude Code Plugin",
  "integrations.tab_codex": "Codex Plugin",
  "integrations.tab_cli": "CLI",
  "integrations.active_accounts": "Active Accounts",
  "integrations.active_agents": "Active Agents",
  "integrations.messages": "Messages",
  "integrations.deliveries": "Deliveries",
  "integrations.errors": "Errors",
  "integrations.disconnects": "Disconnects",
  "integrations.version_distribution": "Version Distribution",
  "integrations.col_version": "Version",
  "integrations.col_count": "Count",
  "integrations.col_share": "Share",
  "integrations.no_version_data": "No version data available",

  // ─── Health Page ───
  "health.title": "System Health",
  "health.description": "Component status and background job monitoring",
  "health.components": "Components",
  "health.api": "API",
  "health.neon": "Neon (DB)",
  "health.kv": "KV Store",
  "health.r2": "R2 Storage",
  "health.checked_at": "Checked at",
  "health.active_alerts": "Active alerts",
  "health.alerts_checked_at": "Alerts checked at",
  "health.alerts_not_checked": "Not checked",
  "health.alerts_failed": "Failed to load system alerts.",
  "health.no_active_alerts": "No active alerts",
  "health.alert_level": "Level",
  "health.alert_title": "Alert",
  "health.alert_detail": "Detail",
  "health.alert_runbook": "Runbook",
  "health.queue_stats": "Queue Stats",
  "health.poison_messages": "Poison Messages",
  "health.pending_jobs": "Pending Jobs",
  "health.running_jobs": "Running Jobs",
  "health.failed_24h": "Failed (24h)",
  "health.cron_tasks": "Cron Tasks",
  "health.col_name": "Name",
  "health.col_status": "Status",
  "health.col_detail": "Detail",

  // ─── Communications Page ───
  "communications.title": "Communications",
  "communications.description": "Send operational broadcasts and track status",
  "communications.tab_compose": "Compose",
  "communications.tab_history": "History",
  "communications.template": "Template",
  "communications.reason": "Reason",
  "communications.params_json": "Params JSON",
  "communications.audience": "Audience",
  "communications.audience_all": "All active users",
  "communications.audience_affected": "Affected users",
  "communications.affected_user_ids": "Affected user IDs",
  "communications.submit": "Send broadcast",
  "communications.submitting": "Sending...",
  "communications.created": "Broadcast accepted: {{id}}",
  "communications.params_invalid": "Params must be a JSON object.",
  "communications.no_broadcasts": "No broadcasts found.",
  "communications.col_broadcast": "Broadcast",
  "communications.col_template": "Template",
  "communications.col_status": "Status",
  "communications.col_progress": "Enqueued / Target (Failed)",
  "communications.col_created": "Created",
  "communications.col_completed": "Completed",
  "communications.detail_requested_by": "Requested by",
  "communications.detail_updated": "Updated",
  "communications.detail_audience": "Audience",
  "communications.detail_params": "Params",
  "communications.template.data_breach_notification": "Data breach",
  "communications.template.privacy_policy_changed": "Privacy policy changed",
  "communications.template.terms_changed": "Terms changed",
  "communications.template.maintenance_window": "Maintenance window",
  "communications.template.incident_alert": "Incident alert",
  "communications.template.incident_resolved": "Incident resolved",
  "communications.template.service_degraded_warning": "Service degraded",

  // ─── Dead Letter Queue Page ───
  "dlq.title": "Dead Letter Queue",
  "dlq.description":
    "Inspect failed queue messages and replay individual entries",
  "dlq.origin_queue": "Origin queue",
  "dlq.from_date": "From date",
  "dlq.to_date": "To date",
  "dlq.search": "Search",
  "dlq.initial_hint": "Choose a queue and date range, then search.",
  "dlq.empty": "No DLQ entries found.",
  "dlq.next_page": "Next page",
  "dlq.col_entry": "Entry",
  "dlq.col_queue": "Queue",
  "dlq.col_partition": "Partition",
  "dlq.col_attempts": "Attempts",
  "dlq.col_error": "Last error",
  "dlq.col_size": "Size",
  "dlq.col_replay": "Replay",
  "dlq.col_uploaded": "Uploaded",
  "dlq.body_bytes": "Body bytes",
  "dlq.uploaded_at": "Uploaded at",
  "dlq.replayed_at": "Replayed at",
  "dlq.not_replayed": "Not replayed",
  "dlq.replay": "Replay entry",
  "dlq.replaying": "Replaying...",

  // ─── Backend API Coverage ───
  "api_coverage.title": "Backend API Test Coverage",
  "api_coverage.description":
    "Lists real backend endpoints with direct API unit tests and backfill status",
  "api_coverage.generated_at": "Generated at",
  "api_coverage.snapshot_failed":
    "Failed to load backend API coverage snapshot.",
  "api_coverage.snapshot_invalid": "Backend API coverage snapshot is invalid.",
  "api_coverage.total_endpoints": "Total endpoints",
  "api_coverage.tested_endpoints": "Direct API unit tested",
  "api_coverage.review_endpoints": "Needs review",
  "api_coverage.missing_endpoints": "Missing tests",
  "api_coverage.total_tests": "All test entries",
  "api_coverage.linked_test_entries": "Linked API unit tests",
  "api_coverage.admin_frontend_exposed": "Admin UI exposed",
  "api_coverage.admin_frontend_unexposed": "Admin UI gaps",
  "api_coverage.linked_tests": "Linked API unit tests",
  "api_coverage.api_modules": "API modules",
  "api_coverage.endpoint_list": "API endpoint list",
  "api_coverage.test_catalog": "Test case entry points",
  "api_coverage.detail_title": "API detail",
  "api_coverage.detail_description": "Standard API docs and test coverage",
  "api_coverage.detail_overview": "Overview",
  "api_coverage.detail_contract": "Input / output",
  "api_coverage.code_facts": "Code-extracted facts",
  "api_coverage.back_to_list": "Back to API list",
  "api_coverage.endpoint_not_found": "API endpoint not found.",
  "api_coverage.filtered_count": "{{visible}} / {{total}} shown",
  "api_coverage.api_endpoint": "API endpoint",
  "api_coverage.api_name": "API name",
  "api_coverage.module": "Module",
  "api_coverage.category": "Category",
  "api_coverage.test_status": "Tested",
  "api_coverage.frontend_exposure": "Admin UI exposure",
  "api_coverage.frontend_calls": "Admin frontend calls",
  "api_coverage.api_doc": "API docs",
  "api_coverage.auth": "Auth",
  "api_coverage.status_codes": "Status codes",
  "api_coverage.response": "Response",
  "api_coverage.request_fields": "Request fields",
  "api_coverage.error_codes": "Error codes",
  "api_coverage.middleware": "Middleware / rate limits",
  "api_coverage.headers": "Header inputs",
  "api_coverage.path_params": "Path params",
  "api_coverage.query_params": "Query params",
  "api_coverage.body_reads": "Body reads",
  "api_coverage.validators": "Validators",
  "api_coverage.validation_checks": "Validation / guards",
  "api_coverage.response_facts": "Response shape",
  "api_coverage.response_json": "JSON content",
  "api_coverage.env_bindings": "Worker bindings / DO",
  "api_coverage.service_calls": "Service calls",
  "api_coverage.logic_steps": "Code logic",
  "api_coverage.field_name": "Field",
  "api_coverage.field_type": "Type",
  "api_coverage.field_required": "Required",
  "api_coverage.field_description": "Description",
  "api_coverage.field_type.header": "HTTP Header",
  "api_coverage.field_type.path": "Path param",
  "api_coverage.field_type.query": "Query param",
  "api_coverage.field_type.body": "Body",
  "api_coverage.code_read": "Code-read",
  "api_coverage.code_field.header":
    "Real input read by backend code from the request headers.",
  "api_coverage.code_field.path":
    "Real input read by backend code from the route path.",
  "api_coverage.code_field.query":
    "Real input read by backend code from the URL query string.",
  "api_coverage.code_field.body": "Request body read mode: {{value}}.",
  "api_coverage.fact.reads_json": "Reads JSON body",
  "api_coverage.fact.reads_valid_json": "Reads validated JSON body",
  "api_coverage.fact.reads_form": "Reads multipart/form body",
  "api_coverage.fact.reads_binary": "Reads binary body",
  "api_coverage.fact.reads_text": "Reads text body",
  "api_coverage.fact.reads_url": "Reads URL / query",
  "api_coverage.logic.validate_ws":
    "Validate that the Upgrade header is websocket",
  "api_coverage.logic.read_query": "Read query parameters",
  "api_coverage.logic.parse_json": "Parse request JSON",
  "api_coverage.logic.validate_schema": "Validate request schema",
  "api_coverage.logic.read_path": "Read path parameters",
  "api_coverage.logic.route_do": "Route to Durable Object",
  "api_coverage.logic.attach_expected_agent_id":
    "Pass expected_agent_id to AgentConnectionDO for first-frame validation",
  "api_coverage.logic.proxy_downstream":
    "Proxy the upgrade request to the downstream runtime",
  "api_coverage.logic.wait_until": "Schedule async side effect",
  "api_coverage.logic.return_json": "Return JSON response",
  "api_coverage.validation.upgrade":
    "Upgrade header must be equal to websocket",
  "api_coverage.validation.schema": "Schema validation is enforced",
  "api_coverage.validation.app_error":
    "Business errors are returned through AppError",
  "api_coverage.validation.path_params": "Path parameters are read",
  "api_coverage.validation.query_params": "Query parameters are read",
  "api_coverage.response.proxy":
    "Proxy Durable Object / downstream runtime response",
  "api_coverage.response.json": "Returns JSON response",
  "api_coverage.response.text": "Returns text response",
  "api_coverage.response.raw": "Returns raw body response",
  "api_coverage.response.redirect": "Returns redirect response",
  "api_coverage.backfill": "Backfill status",
  "api_coverage.no_linked_tests": "No direct API unit test case found.",
  "api_coverage.untitled_endpoint": "Untitled API",
  "api_coverage.doc_found": "Linked to API Design",
  "api_coverage.doc_missing": "Missing from API Design",
  "api_coverage.doc_value_missing": "Not listed in docs",
  "api_coverage.no_error_codes": "No error code extracted from API docs.",
  "api_coverage.none": "None",
  "api_coverage.test_count": "{{count}} test entries",
  "api_coverage.frontend_call_count": "{{count}} calls",
  "api_coverage.frontend_exposed": "Exposed by Admin UI ({{count}} calls)",
  "api_coverage.frontend_unexposed": "Not exposed by Admin UI",
  "api_coverage.frontend_not_applicable": "Not an Admin endpoint",
  "api_coverage.module_filter.all": "All modules",
  "api_coverage.search_placeholder": "Search API path, method, or test title",
  "api_coverage.tree_search_placeholder":
    "Search module, path, method, or API name",
  "api_coverage.open_test_catalog": "View all tests",
  "api_coverage.test_search_placeholder": "Search test title or test logic",
  "api_coverage.page_count": "Page {{page}} / {{total}}",
  "api_coverage.linked_endpoint_count": "Linked APIs: {{count}}",
  "api_coverage.method.all": "All methods",
  "api_coverage.method.GET": "GET",
  "api_coverage.method.POST": "POST",
  "api_coverage.method.PUT": "PUT",
  "api_coverage.method.PATCH": "PATCH",
  "api_coverage.method.DELETE": "DELETE",
  "api_coverage.method.ALL": "ALL",
  "api_coverage.scope.all": "All scopes",
  "api_coverage.scope.admin": "Admin",
  "api_coverage.scope.public_api": "Public API",
  "api_coverage.scope.internal": "Internal API",
  "api_coverage.scope.websocket": "WebSocket",
  "api_coverage.status.all": "All statuses",
  "api_coverage.status.tested": "Tested",
  "api_coverage.status.needs_review": "Needs review",
  "api_coverage.status.missing": "Missing",
  "api_coverage.strength.explicit": "Explicit",
  "api_coverage.strength.domain": "Same module",
  "api_coverage.backfill.tested": "Explicit API test entry exists.",
  "api_coverage.backfill.needs_review":
    "Only same-module tests were found; confirm whether they cover this API.",
  "api_coverage.backfill.missing":
    "Add a test that calls this API directly and asserts main and error branches.",

  // ─── Backend API Tests ───
  "api_tests.title": "Backend API Test Cases",
  "api_tests.description":
    "Browse tests by unit, integration, security, critical path, and end-to-end scenario, with each category's validation goal.",
  "api_tests.open_api_coverage": "View API coverage",
  "api_tests.test_tree": "Test categories",
  "api_tests.tree_search_placeholder":
    "Search test type, path, title, or logic",
  "api_tests.test_not_found": "Test case not found.",
  "api_tests.test_type": "Test type",
  "api_tests.test_goal": "Test goal",
  "api_tests.test_method": "Execution method",
  "api_tests.source_path": "Test entry",
  "api_tests.test_logic": "Test logic",
  "api_tests.linked_apis": "Directly linked APIs",
  "api_tests.no_linked_apis":
    "This test is not used as direct unit coverage evidence for a specific API.",
  "api_tests.type_tabs": "Test scenario tabs",
  "api_tests.tab_stats": "{{files}} files · {{tests}} tests",
  "api_tests.file_index": "File index",
  "api_tests.file_count": "{{files}} / {{total}} files",
  "api_tests.file_search_placeholder": "Search file path, test title, or logic",
  "api_tests.file_tests_count": "{{tests}} tests · {{apis}} linked APIs",
  "api_tests.file_not_found": "Test file not found.",
  "api_tests.file_tabs": "File detail tabs",
  "api_tests.file_tab_cases": "Test cases",
  "api_tests.file_tab_apis": "Linked APIs",
  "api_tests.file_total_cases": "{{tests}} test cases",
  "api_tests.file_total_apis": "{{apis}} linked APIs",
  "api_tests.type.api_unit": "API unit test",
  "api_tests.type.unit": "Unit / component test",
  "api_tests.type.integration": "Integration test",
  "api_tests.type.security": "Security test",
  "api_tests.type.critical_path": "Critical path test",
  "api_tests.type.performance": "Performance / baseline test",
  "api_tests.type.desktop_e2e": "Desktop E2E",
  "api_tests.type.maestro": "Native Maestro flow",
  "api_tests.type.other": "Other test",
  "api_tests.goal.api_unit":
    "Goal: call one API directly and verify that API's input, validation, main response, and error branches. The API coverage page only uses this category as direct linkage.",
  "api_tests.goal.unit":
    "Goal: verify local function, service, store, or UI component logic; it does not mean a specific API contract has direct coverage.",
  "api_tests.goal.integration":
    "Goal: verify collaboration across multiple APIs, DB, KV, Queue, DO, or business flows; not direct coverage for one API.",
  "api_tests.goal.security":
    "Goal: verify authentication, authorization, isolation, leak prevention, and attack-surface controls, usually across multiple APIs or resources.",
  "api_tests.goal.critical_path":
    "Goal: verify release-critical business journeys and end-to-end outcomes instead of a single API contract.",
  "api_tests.goal.performance":
    "Goal: verify latency, throughput, resource use, and regression baselines; this does not prove full functional contract coverage.",
  "api_tests.goal.desktop_e2e":
    "Goal: verify real desktop user flows and shell integration for client acceptance, not direct API coverage.",
  "api_tests.goal.maestro":
    "Goal: verify iOS / Android visible user flows, cross-device behavior, and UI state.",
  "api_tests.goal.other":
    "Goal: auxiliary or tooling tests; use the title and entry path to determine validation scope.",

  // ─── Crashes ───
  "crashes.title": "Crash Reports",
  "crashes.description":
    "Grouped crash reports from the self-hosted /v1/crash endpoint",
  "crashes.filter_platform": "Platform",
  "crashes.filter_since": "Since",
  "crashes.filter_all": "All",
  "crashes.since_24h": "24h",
  "crashes.since_7d": "7 days",
  "crashes.since_30d": "30 days",
  "crashes.no_groups": "No crashes in this window.",
  "crashes.col_error": "Error",
  "crashes.col_platform": "Platform",
  "crashes.col_count": "Count",
  "crashes.col_first_seen": "First seen",
  "crashes.col_last_seen": "Last seen",
  "crashes.samples_title": "Recent samples",
  "crashes.back_to_list": "Back to list",
  "crashes.sample_stack": "Stack",
  "crashes.sample_breadcrumbs": "Breadcrumbs",
  "crashes.sample_extra": "Extra context",
  "crashes.sample_app_version": "App version",
  "crashes.sample_os_version": "OS version",
  "crashes.sample_is_fatal": "Fatal",
  "crashes.sample_component_stack": "Component stack",
  "crashes.sample_device_hash": "Device hash",
  "crashes.sample_user_hash": "User hash",
  "crashes.sample_client_time": "Client time",
  "crashes.group_count": "{{count}} occurrences",

  // ─── Relations ───
  "relations.title": "Relations",
  "relations.description": "View and audit user relationships",
  "relations.col_type": "Type",
  "relations.col_user_a": "User A",
  "relations.col_user_b": "User B / Agent",
  "relations.col_status": "Status",
  "relations.col_conversation": "Conversation",
  "relations.col_created": "Created",
  "relations.all_types": "All Types",
  "relations.all_status": "All Status",
  "relations.search_placeholder": "Search by name or email...",
  "relations.no_relations": "No relations found.",
  "relations.page_info":
    "{{total}} relation(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Blocks ───
  "blocks.title": "Blocks",
  "blocks.description": "View block records for abuse investigation",
  "blocks.col_blocker": "Blocker",
  "blocks.col_blocked": "Blocked",
  "blocks.col_created": "Created",
  "blocks.blocker_placeholder": "Blocker UUID...",
  "blocks.blocked_placeholder": "Blocked UUID...",
  "blocks.no_blocks": "No blocks found.",
  "blocks.page_info":
    "{{total}} block(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Conversations ───
  "conversations.title": "Conversations",
  "conversations.description": "Diagnose conversation state and members",
  "conversations.col_id": "ID",
  "conversations.col_type": "Type",
  "conversations.col_members": "Members",
  "conversations.col_last_activity": "Last Activity",
  "conversations.col_frozen": "Frozen",
  "conversations.col_terminated": "Terminated",
  "conversations.col_created": "Created",
  "conversations.all_types": "All Types",
  "conversations.all_frozen": "All",
  "conversations.frozen_yes": "Frozen",
  "conversations.frozen_no": "Not Frozen",
  "conversations.all_terminated": "All",
  "conversations.terminated_yes": "Terminated",
  "conversations.terminated_no": "Not Terminated",
  "conversations.search_placeholder": "Conversation ID...",
  "conversations.no_conversations": "No conversations found.",
  "conversations.page_info":
    "{{total}} conversation(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Conversation Detail ───
  "conv_detail.title": "Conversation Detail",
  "conv_detail.info": "Info",
  "conv_detail.members": "Members",
  "conv_detail.message_stats": "Message Statistics",
  "conv_detail.col_member": "Member",
  "conv_detail.col_type": "Type",
  "conv_detail.col_unread": "Unread",
  "conv_detail.col_muted": "Muted",
  "conv_detail.col_pinned": "Pinned",
  "conv_detail.col_hidden": "Hidden",
  "conv_detail.col_joined": "Joined",
  "conv_detail.total_messages": "Total Messages",
  "conv_detail.recalled": "Recalled",
  "conv_detail.by_type": "By Content Type",
  "conv_detail.by_sender": "By Sender Type",
  "conv_detail.not_found": "Conversation not found.",

  // ─── Messages Tab ───
  "messages_tab.title": "Messages",
  "messages_tab.total_messages": "Total Messages",
  "messages_tab.human_ratio": "Human %",
  "messages_tab.agent_ratio": "Agent %",
  "messages_tab.recall_rate": "Recall Rate",
  "messages_tab.daily_trends": "Daily Message Trends",
  "messages_tab.type_distribution": "Content Type Distribution",
  "messages_tab.col_date": "Date",
  "messages_tab.col_human": "Human",
  "messages_tab.col_agent": "Agent",
  "messages_tab.col_system": "System",
  "messages_tab.col_total": "Total",
  "messages_tab.col_content_type": "Content Type",
  "messages_tab.col_count": "Count",
  "messages_tab.col_percentage": "%",

  // ─── Sidebar: Invites ───
  "nav.invites": "Invites",
  "nav.invites.applications": "Applications",
  "nav.invites.codes": "Invite Codes",

  // ─── Applications ───
  "applications.title": "Applications",
  "applications.description": "Manage invite-only registration applications",
  "applications.tab_all": "All",
  "applications.tab_pending": "Pending",
  "applications.tab_approved": "Approved",
  "applications.tab_rejected": "Rejected",
  "applications.col_email": "Email",
  "applications.col_name": "Name",
  "applications.col_role": "Role",
  "applications.col_reason": "Reason",
  "applications.col_status": "Status",
  "applications.col_date": "Date",
  "applications.col_actions": "Actions",
  "applications.approve": "Approve",
  "applications.reject": "Reject",
  "applications.reject_title": "Reject Application",
  "applications.reject_desc":
    "Please provide a reason for rejecting this application.",
  "applications.no_applications": "No applications found.",
  "applications.page_info":
    "{{total}} application(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Invite Codes ───
  "invite_codes.title": "Invite Codes",
  "invite_codes.description": "Manage invite codes for registration",
  "invite_codes.create_code": "Create Code",
  "invite_codes.all_status": "All Status",
  "invite_codes.status_available": "Available",
  "invite_codes.status_claimed": "Claimed",
  "invite_codes.status_expired": "Expired",
  "invite_codes.col_code": "Code",
  "invite_codes.col_issued_by": "Issued By",
  "invite_codes.col_claimed_by": "Claimed By",
  "invite_codes.col_status": "Status",
  "invite_codes.col_created": "Created",
  "invite_codes.col_expires": "Expires",
  "invite_codes.never": "Never",
  "invite_codes.no_codes": "No invite codes found.",
  "invite_codes.page_info":
    "{{total}} code(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Space Reports (P4) ───
  "space_reports.title": "Space Reports",
  "space_reports.description":
    "Moderate user-reported public Space items. Upholding a pending report takes the item down.",
  "space_reports.empty": "No reports match the current filter.",
  "space_reports.col_item": "Item",
  "space_reports.col_reason": "Reason",
  "space_reports.col_status": "Status",
  "space_reports.col_reported_at": "Reported",
  "space_reports.col_actions": "Actions",
  "space_reports.action_uphold": "Uphold + Takedown",
  "space_reports.action_dismiss": "Dismiss",
  "space_reports.confirm_uphold":
    "Uphold this report and take the item down? This writes an audit row and cannot be reversed from this screen.",
  "space_reports.confirm_dismiss":
    "Dismiss this report? The item stays live. Writes an audit row.",
  "space_reports.taken_down_at": "Taken down at",
  "space_reports.filter_pending": "Pending",
  "space_reports.filter_auto_upheld": "Auto-takedown",
  "space_reports.filter_upheld": "Upheld",
  "space_reports.filter_dismissed": "Dismissed",
  "space_reports.filter_all": "All",
  "space_reports.status_pending": "Pending",
  "space_reports.status_auto_upheld": "Auto-takedown",
  "space_reports.status_upheld": "Upheld",
  "space_reports.status_dismissed": "Dismissed",
  "space_reports.reason_spam": "Spam",
  "space_reports.reason_harassment": "Harassment",
  "space_reports.reason_nsfw": "NSFW",
  "space_reports.reason_illegal": "Illegal",
  "space_reports.reason_misinformation": "Misinformation",
  "space_reports.reason_other": "Other",

  // ─── Date Picker ───
  "date_picker.date": "Date",
  "date_picker.7d": "7 Days",
  "date_picker.30d": "30 Days",
  "date_picker.90d": "90 Days",
  "date_picker.1y": "1 Year",
  "date_picker.custom": "Custom",
};
